<template>
  <vue-resizable
    data-cy="incoming-sms"
    class="z-[101]"
    :class="{
      invisible: !smsActive,
      'overflow-hidden rounded-lg': isMinimized,
    }"
    drag-selector=".draggable"
    :width="smsOptions.width"
    :height="smsOptions.height"
    :min-width="smsOptions.minWidth"
    :min-height="smsOptions.minHeight"
    :top="smsOptions.top"
    :left="smsOptions.left"
    @drag:start="dragging = true"
    @drag:end="updatePosition"
    @resize:end="updatePosition"
  >
    <div
      ref="draggable"
      class="relative rounded-lg bg-grey-navigation text-white shadow-dialer"
      :class="isMinimized ? 'max-w-55' : 'h-full'"
    >
      <div
        class="group/header relative flex h-full w-full flex-col items-start justify-between"
        :class="{ 'max-w-55': isMinimized }"
      >
        <div
          class="draggable inline-flex cursor-pointer items-start justify-start gap-3 self-stretch border-b border-white/10 p-4"
        >
          <div class="flex w-full items-start justify-between">
            <div
              class="flex flex-1 flex-col items-start justify-center gap-y-2"
            >
              <template v-if="!isMinimized">
                <div class="header-row group" data-cy="recipient">
                  <div class="left">{{ $t("COMMON.TO") }}:</div>
                  <div
                    v-if="smsRecipient"
                    class="right flex items-center gap-x-1 text-xxs font-semibold tracking-[0.3px]"
                  >
                    <span class="uppercase">
                      {{ smsRecipient.name }}
                    </span>
                    <span v-if="smsRecipient.number" class="text-white/80">
                      {{ `(${smsRecipient.number})` }}
                    </span>
                    <copy-to-clipboard-button
                      v-if="smsRecipient.number"
                      data-cy="copy-recipient-number"
                      class="hidden group-hover:block"
                      :value="smsRecipient.number"
                      @click.stop
                    />
                    <call-client-component
                      data-cy="call-client"
                      class="hidden group-hover:block"
                      :can-call="!!smsRecipient.number"
                      :telephone-number="smsRecipient.number"
                      :size-classes="{ circle: 'size-[22px]', icon: 12 }"
                      @call="handleCallClient"
                    />
                  </div>
                </div>
                <div class="header-row group">
                  <div class="left">{{ $t("COMMON.FROM") }}:</div>
                  <div class="right">
                    <sms-from-number />
                  </div>
                </div>
                <div
                  v-if="activeSmsConversation?.application_business_name"
                  class="header-row group"
                  data-cy="business-name"
                >
                  <div class="left">{{ $t("COMMON.DEAL") }}:</div>
                  <div
                    class="right flex items-center gap-x-2 text-xs leading-none font-semibold"
                  >
                    {{ activeSmsConversation.application_business_name }}
                    <router-link
                      v-if="dealLink"
                      v-tooltip="{
                        content: `${$t('COMMON.OPEN')} ${$t('COMMON.DEAL')}`,
                      }"
                      data-cy="deal-link"
                      :to="dealLink"
                      class="hidden group-hover:block"
                    >
                      <icon-base
                        width="20"
                        height="20"
                        view-box="0 0 24 24"
                        :icon="IconLink"
                      />
                    </router-link>
                  </div>
                </div>
                <div
                  v-if="activeSmsConversation?.integration_partner"
                  class="header-row"
                >
                  <div class="left">{{ $t("COMMON.PARTNER") }}:</div>
                  <div
                    data-cy="partner-name"
                    class="right flex gap-x-2 text-xs leading-none font-semibold"
                  >
                    {{ activeSmsConversation?.integration_partner }}
                  </div>
                </div>
              </template>
            </div>
            <div class="flex items-start gap-x-2">
              <icon-base
                v-if="isMinimized"
                :icon="IconMaximize"
                icon-name="Maximize"
                class="cursor-pointer text-white"
                data-cy="maximize-call-log-dialog"
                @click="toggleMinimize"
              />
              <icon-base
                v-else
                :icon="IconSubtract"
                view-box="0 -5 16 16"
                icon-name="Minimize"
                class="cursor-pointer text-white"
                data-cy="minimize-call-log-dialog"
                @click="toggleMinimize"
              />
              <icon-base
                width="12"
                height="12"
                view-box="0 0 16 16"
                :icon="IconClose"
                class="icon"
                @click="emit('close')"
              />
            </div>
          </div>
        </div>
        <template v-if="!isMinimized">
          <div
            ref="scrollRef"
            class="flex w-full grow flex-col-reverse overflow-y-auto"
          >
            <div class="conversation-messages">
              <div
                v-if="!isLoading && !isFetching"
                v-element-visibility="[
                  loadMoreMessages,
                  {
                    rootMargin: '500px 0px 0px 0px',
                    scrollTarget: scrollRef,
                  },
                ]"
                class="h-1"
              />
              <loader :is-loading="isLoading" />
              <div
                v-for="(conversation, date) in groupedConversation"
                :key="date"
                class="date"
              >
                <span class="text-center text-white/50">
                  <template v-if="isToday(new Date(date))">
                    {{ $t("COMMON.DATES.TODAY") }}
                  </template>
                  <date-time v-else :date="new Date(date).toISOString()" />
                </span>
                <div
                  v-for="message in conversation"
                  :key="message.id"
                  data-cy="sms-row"
                  class="message-wrapper"
                  :class="message.is_outgoing ? 'outgoing' : 'incoming'"
                >
                  <div data-cy="message" class="message">
                    {{ message.message }}
                  </div>
                  <div class="status flex items-end justify-end gap-x-1">
                    <span class="text-xs text-white/60">
                      {{ formatDateCustom(message.created_at, "h:mm a", true) }}
                    </span>
                    <sms-status
                      v-if="message.status"
                      data-cy="sms-status"
                      :status="message.status"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <dynamic-field
            name="smsText"
            class="sms-conversation bottom-0 mt-auto mb-10 max-h-[200px] w-full grow"
            :placeholder="$t('ACTIVITY.SMS.CONVERSATION_PLACEHOLDER')"
            :model-value="smsResponse"
            :type="CommunicationType.sms"
            has-length-counter
            :max-length="MAX_SMS_MESSAGE_LENGTH"
            @clean-content:update="handleCleanContentUpdate"
            @update:model-value="smsResponse = $event"
            @submit="sendSms"
          />
          <div
            ref="sendButtonBlockRef"
            class="flex-no-wrap absolute right-3 bottom-4 flex flex-row items-end gap-x-2"
            :class="isMessageTooLong ? 'justify-between' : 'justify-end'"
          >
            <span v-if="isMessageTooLong" class="text-error">
              {{ $t("ACTIVITY.SMS.TOO_LONG_MESSAGE") }}
            </span>
            <div class="flex items-center space-x-4">
              <icon-button
                data-cy="send-button-sms"
                class="send-button"
                :disabled="isSendingDisabled"
                @click="sendSms"
              >
                <icon-base :icon="IconSend" class="text-white" />
              </icon-button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </vue-resizable>
  <teleport v-if="callDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="callDialog = null"
    />
  </teleport>
</template>

<script setup lang="ts">
import { vElementVisibility } from "@vueuse/components";
import {
  type Ref,
  computed,
  nextTick,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import VueResizable from "vue-resizable";

import isToday from "date-fns/isToday";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import { storeToRefs } from "pinia";

import { CommunicationType } from "@/enums/communicationLogs";
import { stripHTMLTagsFromString } from "@/helpers/common";
import {
  getAssignedPhoneNumber,
  setActiveCallSession,
} from "@/helpers/communication";
import {
  DEFAULT_MINIMIZED_HEIGHT,
  DEFAULT_MINIMIZED_WIDTH,
} from "@/helpers/constants";
import { MAX_SMS_MESSAGE_LENGTH } from "@/helpers/constants/communicationTemplates";
import { formatDateCustom } from "@/helpers/formatting";
import { zeroWidthSpaceCharRegex } from "@/helpers/textEditor";
import { useContacts } from "@/hooks/contacts";
import { useLocalStorageSetting } from "@/hooks/options";
import {
  useReplyToSmsMutation,
  useSmsConversationQuery,
} from "@/hooks/queries/useCommunication";
import { useResizable } from "@/hooks/resizable";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import applicationsApiService from "@/services/modules/applications";
import useSmsStore from "@/stores/sms";

import CopyToClipboardButton from "@/components/CopyToClipboardButton.vue";
import DynamicField from "@/components/DynamicField.vue";
import CallClientComponent from "@/components/communication/CallClient.vue";
import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import SmsFromNumber from "@/components/communicationLogs/communication/sms/SmsFromNumber.vue";
import SmsStatus from "@/components/communicationLogs/communication/sms/SmsStatus.vue";
import IconClose from "@/components/icons/IconClose.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconMaximize from "@/components/icons/IconMaximize.vue";
import IconSend from "@/components/icons/IconSend.vue";
import IconSubtract from "@/components/icons/IconSubtract.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import IconButton from "@/components/ui/buttons/IconButton.vue";

import type {
  ApplicationWithContactData,
  IApplication,
} from "@/models/applications";
import type { CallLog, SmsMessageEventData } from "@/models/communicationLogs";

const { incomingEvent, incomingSmsEvents } = defineProps<{
  incomingEvent: SmsMessageEventData | null;
  incomingSmsEvents: SmsMessageEventData[];
}>();

const emit = defineEmits<{
  close: [];
}>();

const smsOptions = useLocalStorageSetting("smsConversationOptions", {
  top: 0,
  left: 0,
  width: 480,
  height: 680,
  minWidth: 340,
  minHeight: 340,
  minimizedWidth: DEFAULT_MINIMIZED_WIDTH,
  minimizedHeight: DEFAULT_MINIMIZED_HEIGHT,
});
const communicationToolOptions = useLocalStorageSetting(
  "communicationToolOptions",
  {
    x: 0,
    y: 0,
    showAsPopover: false,
  }
);
const assignedPhoneNumber = useLocalStorageSetting("assignedPhoneNumber", null);

const smsActive = ref(true);
const dragging = ref(false);
const isMinimized = ref(false);
const isMessageTooLong = ref(false);
const smsResponse = ref("");
const callDialog = ref<CallLog | null>(null);
const activeConversationDeal = ref<IApplication | null>(null);
const draggable = useTemplateRef("draggable");
const scrollRef = useTemplateRef("scrollRef");

const { updatePosition, repositionAsNeeded } = useResizable(
  smsOptions,
  draggable
);
const { activeSmsConversation } = storeToRefs(useSmsStore());
const activeConversationId = computed(
  () => activeSmsConversation.value?.sms_conversation_id ?? -1
);

const {
  data: conversationData,
  isLoading,
  isFetching,
  refetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  // we have id check in the parent
} = useSmsConversationQuery(activeConversationId);
const replyToSms = useReplyToSmsMutation();

const { callClient } = useContacts(
  activeConversationDeal as Ref<ApplicationWithContactData>
);

const allDataPages = computed(
  () =>
    conversationData.value?.pages?.flatMap(
      (paginatedResponse) => paginatedResponse.data
    ) ?? []
);

const groupedConversation = computed(() => {
  return groupBy(
    orderBy(allDataPages.value, (message) => new Date(message.created_at)),
    (message) => formatDateCustom(message.created_at)
  );
});

const isSendingDisabled = computed(
  () => !smsResponse.value || isMessageTooLong.value
);

const smsRecipient = computed(() => {
  const { from, sender_full_name } = activeSmsConversation.value?.data || {};
  return { name: sender_full_name || "", number: from || "" };
});

const dealLink = computed(() =>
  activeSmsConversation.value?.linked_instance_id
    ? {
        name: ROUTE_DEAL_PROGRESS,
        params: { id: activeSmsConversation.value?.linked_instance_id },
      }
    : undefined
);

const toggleMinimize = async () => {
  if (!isMinimized.value) {
    const { width, height } = smsOptions.value;
    smsOptions.value.restoreHeight = height;
    smsOptions.value.restoreWidth = width;
  }
  isMinimized.value = !isMinimized.value;

  const { width, height, minimizedWidth, minimizedHeight } = smsOptions.value;

  const { restoreWidth, restoreHeight, x, y } = smsOptions.value;

  const setWidth = isMinimized.value ? minimizedWidth : restoreWidth || width;
  const setHeight = isMinimized.value
    ? minimizedHeight
    : restoreHeight || height;
  dragging.value = false;

  if (!x || !y || !setWidth || !setHeight) {
    return;
  }

  await updatePosition({
    left: x,
    top: y,
    width: setWidth,
    height: setHeight,
  });
};

const handleCleanContentUpdate = (cleanContent: string) => {
  smsResponse.value = cleanContent;
  isMessageTooLong.value =
    Math.ceil(cleanContent.length / MAX_SMS_MESSAGE_LENGTH) > 10;
};

const sendSms = async () => {
  if (!smsResponse.value || !activeSmsConversation.value?.sms_log_id) {
    return;
  }
  const from = getAssignedPhoneNumber(assignedPhoneNumber.value) ?? "";
  await replyToSms.mutateAsync({
    id: activeSmsConversation.value?.sms_log_id,
    message: stripHTMLTagsFromString(smsResponse.value || "", false, true)
      .replace(zeroWidthSpaceCharRegex, "")
      .trim(),
    from,
  });
  smsResponse.value = "<p></p>";
};

const loadMoreMessages = async (visible: boolean) => {
  if (
    isLoading.value ||
    isFetchingNextPage.value ||
    !hasNextPage.value ||
    !visible
  ) {
    return;
  }
  await fetchNextPage();
  if (scrollRef.value) {
    await nextTick();
    scrollRef.value.scrollTop += 20;
  }
};

const handleCallClient = async (e?: MouseEvent) => {
  if (!smsRecipient.value.number || !activeConversationDeal.value) {
    return;
  }
  const result = await callClient(e);
  if (!result?.connectionLog?.id) {
    return;
  }
  setActiveCallSession(result);
  if (result.event) {
    communicationToolOptions.value.x = result.event.x;
    communicationToolOptions.value.y = result.event.y;
  }
  callDialog.value = result.connectionLog;
};

const getDealForConversation = async () => {
  if (!activeSmsConversation.value?.linked_instance_id) {
    activeConversationDeal.value = null;
    return;
  }
  activeConversationDeal.value = await applicationsApiService.getApplication(
    activeSmsConversation.value?.linked_instance_id
  );
};

onMounted(async () => {
  await Promise.all([repositionAsNeeded(), getDealForConversation()]);
});

watch(
  () => incomingEvent,
  async (newValue) => {
    if (newValue) {
      await refetch();
    }
  },
  { deep: true }
);

watch(
  () => incomingSmsEvents,
  async (newValue) => {
    if (
      activeSmsConversation.value?.sms_conversation_id ===
      newValue[0]?.sms_conversation_id
    ) {
      await Promise.all([refetch(), getDealForConversation()]);
    }
  },
  { deep: true }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";
.header-row {
  @apply flex h-[22px] w-full items-center space-x-2;

  .left {
    @apply w-1/8 min-w-[45px] flex-none font-semibold text-label;
  }

  .right {
    @apply relative flex w-7/8 justify-start;
    .copy-button {
      @apply hover:bg-transparent;
    }
    :deep() {
      .w-auto.grow-0 {
        @apply ml-0;
      }
      .copy-button {
        @apply hover:bg-transparent;
      }
    }
  }
}

.conversation-messages {
  @apply w-full;
  .date {
    @apply flex w-full flex-col gap-y-4 overflow-y-auto p-4;
    .message-wrapper {
      @apply w-3/4 rounded-t-lg p-2;
      &.incoming {
        @apply mr-auto rounded-br-lg bg-white/16;
      }
      &.outgoing {
        @apply ml-auto rounded-bl-lg bg-[#6C73A5];
      }
    }
  }
}
.sms-info-row {
  @apply inline-flex items-center justify-start gap-2 self-stretch;
  .icon {
    @apply flex-none text-label;
  }
}
:deep() {
  .send-button[disabled] {
    @apply bg-disabled-medium;
  }
  .ql-toolbar {
    display: none;
  }
  .sms-status svg {
    @apply text-white;
  }
  .vue3-circular-progressbar {
    @apply -left-2;
  }
}
</style>
